<script setup lang="ts">
    import { useForm } from '@inertiajs/vue3';
    import { Voucher } from '@/types';
    import DialogForm from '@/components/DialogForm.vue';
    import { useI18n } from 'vue-i18n';
    import { Button } from '@/components/ui/button';
    import { ChevronRight } from 'lucide-vue-next';
    import VoucherForm from './VoucherForm.vue';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';

    const { t } = useI18n();

    const props = defineProps<{
        voucher: Voucher;
        buttonText: string;
        title: string;
    }>();

    const queryClient = useQueryClient();

    const dimensionValues = (props.voucher as any).dimension_values || (props.voucher as any).dimensionValues;
    const initialDimensionValues: any[] = [];
    if (dimensionValues && Array.isArray(dimensionValues)) {
        dimensionValues.forEach((dimValue: any) => {
            initialDimensionValues.push(dimValue);
        });
    }

    const form = useForm({
        id: props.voucher.id,
        comment: props.voucher.comment,
        AccountHandle_Number: props.voucher.AccountHandle_Number,
        no_voucher: Number(props.voucher.no_voucher || 0),
        file: [],
        dimension_values: initialDimensionValues,
    });

    const handleError = (error: any) => {
        queryClient.invalidateQueries({ queryKey: [QueryKey.VOUCHERS, props.voucher.company_id] });
    };

    const { hasPermission } = usePermissions();
    const updateRoute = hasPermission(Permission.VOUCHERS_OWN_UPDATE) && !hasPermission(Permission.VOUCHERS_UPDATE)
        ? 'v2.voucher.own.store'
        : 'v2.voucher.store';
</script>

<template>
    <DialogForm
        type="update"
        :form="form"
        :route="route(updateRoute)"
        method="post"
        :query-key="[QueryKey.VOUCHERS, voucher.company_id]"
        :title="title"
        modal-size="sm:max-w-xl"
        :button-text="buttonText"
        @error="handleError"
    >
        <VoucherForm
            :voucher="voucher"
            :form="form"
        />

        <template #trigger>
            <Button
                variant="edit"
                size="xs"
            >
                <span>{{ t(buttonText) }}</span>
                <ChevronRight />
            </Button>
        </template>
    </DialogForm>
</template>
